import { useState, createContext, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { loginFields } from "../Login/LoginFields";
import { toast } from "react-toastify";
import CryptoJS from "crypto-js";
import { scheduleTokenRefresh } from "../utils/tokenUtils.js";
import api from "../api";

export const AppContext = createContext();
export const AppContextProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [toggle, setToggle] = useState(true);
  const [studyMaterials, setStudyMaterials] = useState({});
  const [loading, setLoading] = useState(true);
  const [allGalleryImages, setAllGalleryImages] = useState([]);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  let errorShown = useRef(false);
  const secretKey = process.env.REACT_APP_SECRET_KEY || "mySecretKey123";
  axios.defaults.withCredentials = true;
  const backendUrl =
    window._env_?.REACT_APP_BACKEND_URL || "http://localhost:4500";
  const [updateOmbudsmanGrievance, setUpdateOmbudsmanGrievance] =
    useState(false);
  const navigate = useNavigate();
  const [loginValidationError, setLoginValidationError] = useState({
    email: null,
    password: null,
  });
  const iserrorShown = useRef(false);

  const encryptPassword = (password) => {
    return CryptoJS.AES.encrypt(password, secretKey).toString();
  };

  const [loginData, setLoginData] = useState(
    loginFields.reduce((acc, field) => {
      acc[field.fieldName] = "";
      return acc;
    }, {})
  );

  const handleChange = (fieldName, value) => {
    setLoginData((prevState) => ({
      ...prevState,
      [fieldName]: value,
    }));

    setLoginValidationError((prev) => ({
      ...prev,
      [fieldName]: null,
    }));
  };

  const fieldLabels = {
    email: "Email",
    password: "Password",
  };

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const transformStudyMaterials = (materials) => {
    const transformed = {};

    materials.forEach((courseData) => {
      const { courseName, documents } = courseData;

      if (documents && documents.length > 0) {
        transformed[courseName] = documents.map((doc) => ({
          name: doc.documentName,
          id: doc.id,
          contentType: doc.contentType,
          data: doc.data,
          originalFilename: doc.originalfilename,
        }));
      }
    });

    return transformed;
  };

  const fetchAllGalleryImages = useCallback(
    async () => {
    try {
      const { data } = await api.get(
        `${backendUrl}/api/auth/getAllGalleryImages`
      );
      if (data.success) {
        const formattedImages = data.data
          .filter((img) => img.isActive)
          .map((img) => ({
            ...img,
            url: `data:${img.contentType};base64,${img.data}`,
          }));
        setAllGalleryImages(formattedImages);
      }
      errorShown.current = false;
    } catch (error) {
      if (errorShown.current) return;
      errorShown.current = true;
      if (error.response && error.response.status === 503) {
        toast.error(
          "⚠️ The service is currently under maintenance. Please try again later."
        );
      } else if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else {
        console.error("Unexpected error:", error);
        toast.error("Something went wrong.");
      }
    } 
  }, [backendUrl]
  )

 const fetchStudyMaterials = useCallback( async ()=> {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${backendUrl}/api/auth/getAllStudyMaterials`
      );
      if (data.success) {
        const transformedData = transformStudyMaterials(data.materials);
        setStudyMaterials(transformedData);
      }
    } catch (error) {
      console.error("Error fetching study materials:", error);
      setStudyMaterials({});
    } finally {
      setLoading(false);
    }
  },[backendUrl])

  const validateField = (name, value) => {
    const trimmed = value?.trim();
    if (!trimmed) return `${fieldLabels[name]} is required`;
    if (name === "email" && !emailRegex.test(trimmed)) {
      return "Enter a valid email address";
    }
    return null;
  };

  const handleSubmit = async (e, onLoadingChange) => {
    if (onLoadingChange) onLoadingChange(true);
    e.preventDefault();
    const errors = {};
    let hasError = false;

    Object.entries(loginData).forEach(([key, value]) => {
      const error = validateField(key, value);
      if (error) {
        errors[key] = error;
        hasError = true;
      }
    });

    setLoginValidationError(errors);

    if (hasError) return;

    if (!backendUrl) {
      toast.error("Backend URL is not defined. Check your .env file.");
      return;
    }
    try {
      const encryptedPassword = encryptPassword(loginData.password);
      const encryptedLoginData = { ...loginData, password: encryptedPassword };
      axios.defaults.withCredentials = true;

      const { data } = await api.post(
        `${backendUrl}/api/auth/login`,
        encryptedLoginData,
        { withCredentials: true }
      );
      if (data.success) {
        localStorage.setItem("authToken", data.token);
        axios.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
        scheduleTokenRefresh(data.token);
        navigate("/admin");

        setLoginData(
          loginFields.reduce((acc, field) => {
            acc[field.fieldName] = "";
            return acc;
          }, {})
        );
        setLoginValidationError({ email: null, password: null }); // Clear errors on success
      }
    } catch (error) {
      // Handle specific backend error messages for invalid credentials
      if (error.response && error.response.data) {
        const errorMessage = error.response.data.message;

        if (errorMessage === "Invalid email") {
          setLoginValidationError({
            email: "Invalid email address",
            password: null,
          });
          toast.error("Invalid email address");
        } else if (errorMessage === "Invalid Password") {
          setLoginValidationError({
            email: null,
            password: "Invalid password",
          });
          toast.error("Invalid password");
        } else if (error.response.status === 503) {
          toast.error(
            "⚠️ The service is currently under maintenance. Please try again later."
          );
        } else {
          // For other backend errors, show the message from backend
          toast.error(errorMessage || "Login failed");
        }
      } else if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else {
        console.error("Unexpected error:", error);
        toast.error("Something went wrong.");
      }
    } finally {
      if (onLoadingChange) onLoadingChange(false);
    }
  };

  function toggleSidebar() {
    setToggle(!toggle);
  }

  const handleSubmenuToggle = (menuName) => {
    setOpenSubmenu(openSubmenu === menuName ? null : menuName);
  };

  useEffect(() => {
    if (!navigator.onLine) {
      toast.error("You are offline. Please check your internet connection.");
      iserrorShown.current = true;
    }
    fetchStudyMaterials();
    fetchAllGalleryImages();
  }, [fetchStudyMaterials, fetchAllGalleryImages]);

  const value = {
    backendUrl,
    loginData,
    setLoginData,
    loginValidationError,
    handleChange,
    handleSubmit,
    toggleSidebar,
    handleSubmenuToggle,
    toggle,
    setToggle,
    openSubmenu,
    setOpenSubmenu,
    iserrorShown,
    updateOmbudsmanGrievance,
    setUpdateOmbudsmanGrievance,
    isLoading,
    setIsLoading,
    setLoading,
    loading,
    setStudyMaterials,
    studyMaterials,
    fetchStudyMaterials,
    allGalleryImages,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
